import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils';

const SystemHealthPanel = () => {
  const [systemMetrics, setSystemMetrics] = useState({
    uptime: 99.8,
    responseTime: 245,
    errorRate: 0.2,
    activeUsers: 342,
    serverLoad: 45,
    memoryUsage: 68,
    diskUsage: 34,
    networkLatency: 12
  });

  const [systemAlerts, setSystemAlerts] = useState([
    {
      id: 1,
      type: 'warning',
      title: 'High Memory Usage',
      message: 'Memory usage is above 80% threshold',
      timestamp: '2024-03-15T10:30:00Z',
      status: 'active'
    },
    {
      id: 2,
      type: 'info',
      title: 'Scheduled Maintenance',
      message: 'System maintenance scheduled for tonight at 2:00 AM',
      timestamp: '2024-03-15T09:15:00Z',
      status: 'scheduled'
    },
    {
      id: 3,
      type: 'error',
      title: 'Payment Gateway Issue',
      message: 'Payment processing delays detected',
      timestamp: '2024-03-15T08:45:00Z',
      status: 'resolved'
    }
  ]);

  const [performanceData, setPerformanceData] = useState([
    { metric: 'API Response Time', value: '245ms', status: 'good', target: '<300ms' },
    { metric: 'Database Query Time', value: '89ms', status: 'excellent', target: '<100ms' },
    { metric: 'Image Load Time', value: '1.2s', status: 'good', target: '<2s' },
    { metric: 'Search Response Time', value: '156ms', status: 'good', target: '<200ms' }
  ]);

  const getAlertColor = (type) => {
    switch (type) {
      case 'error':
        return 'bg-red-100 text-red-600';
      case 'warning':
        return 'bg-yellow-100 text-yellow-600';
      case 'info':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error':
        return 'AlertCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'info':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent':
        return 'text-green-600';
      case 'good':
        return 'text-blue-600';
      case 'warning':
        return 'text-yellow-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getHealthColor = (value, threshold = 80) => {
    if (value >= threshold) return 'text-red-600';
    if (value >= threshold * 0.8) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-2xl text-text-primary">
          System Health
        </h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-text-secondary">System Operational</span>
          </div>
          <Button variant="primary" iconName="RefreshCw">
            Refresh
          </Button>
        </div>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Uptime</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {systemMetrics.uptime}%
              </p>
              <p className="text-sm text-text-muted mt-1">Last 30 days</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={24} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Response Time</p>
              <p className={`text-2xl font-bold mt-1 ${getHealthColor(systemMetrics.responseTime, 500)}`}>
                {systemMetrics.responseTime}ms
              </p>
              <p className="text-sm text-text-muted mt-1">Average</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Error Rate</p>
              <p className={`text-2xl font-bold mt-1 ${getHealthColor(systemMetrics.errorRate, 1)}`}>
                {systemMetrics.errorRate}%
              </p>
              <p className="text-sm text-text-muted mt-1">Last 24 hours</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={24} className="text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-secondary text-sm font-medium">Active Users</p>
              <p className="text-2xl font-bold text-primary mt-1">
                {systemMetrics.activeUsers}
              </p>
              <p className="text-sm text-text-muted mt-1">Current</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={24} className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Resource Usage */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
          Resource Usage
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="mb-2">
              <div className="text-2xl font-bold text-text-primary">
                {systemMetrics.serverLoad}%
              </div>
              <div className="text-sm text-text-secondary">Server Load</div>
            </div>
            <div className="w-full bg-surface-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  systemMetrics.serverLoad > 80 ? 'bg-red-500' : 
                  systemMetrics.serverLoad > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${systemMetrics.serverLoad}%` }}
              ></div>
            </div>
          </div>

          <div className="text-center">
            <div className="mb-2">
              <div className="text-2xl font-bold text-text-primary">
                {systemMetrics.memoryUsage}%
              </div>
              <div className="text-sm text-text-secondary">Memory Usage</div>
            </div>
            <div className="w-full bg-surface-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  systemMetrics.memoryUsage > 80 ? 'bg-red-500' : 
                  systemMetrics.memoryUsage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${systemMetrics.memoryUsage}%` }}
              ></div>
            </div>
          </div>

          <div className="text-center">
            <div className="mb-2">
              <div className="text-2xl font-bold text-text-primary">
                {systemMetrics.diskUsage}%
              </div>
              <div className="text-sm text-text-secondary">Disk Usage</div>
            </div>
            <div className="w-full bg-surface-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  systemMetrics.diskUsage > 80 ? 'bg-red-500' : 
                  systemMetrics.diskUsage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${systemMetrics.diskUsage}%` }}
              ></div>
            </div>
          </div>

          <div className="text-center">
            <div className="mb-2">
              <div className="text-2xl font-bold text-text-primary">
                {systemMetrics.networkLatency}ms
              </div>
              <div className="text-sm text-text-secondary">Network Latency</div>
            </div>
            <div className="w-full bg-surface-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  systemMetrics.networkLatency > 50 ? 'bg-red-500' : 
                  systemMetrics.networkLatency > 30 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(systemMetrics.networkLatency * 2, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
          Performance Metrics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {performanceData.map((metric, index) => (
            <div key={index} className="flex items-center justify-between p-4 border border-border-light rounded-lg">
              <div>
                <div className="font-medium text-text-primary">{metric.metric}</div>
                <div className="text-sm text-text-secondary">Target: {metric.target}</div>
              </div>
              <div className="text-right">
                <div className={`text-lg font-bold ${getStatusColor(metric.status)}`}>
                  {metric.value}
                </div>
                <div className={`text-sm ${getStatusColor(metric.status)}`}>
                  {metric.status}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* System Alerts */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="px-6 py-4 border-b border-border-light">
          <h3 className="font-heading font-semibold text-lg text-text-primary">
            System Alerts
          </h3>
        </div>
        
        <div className="divide-y divide-border-light">
          {systemAlerts.map((alert) => (
            <div key={alert.id} className="p-6">
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getAlertColor(alert.type)}`}>
                  <Icon name={getAlertIcon(alert.type)} size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-text-primary">{alert.title}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      alert.status === 'active' ? 'bg-red-100 text-red-600' :
                      alert.status === 'resolved'? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {alert.status}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mb-2">{alert.message}</p>
                  <p className="text-xs text-text-muted">{formatDate(alert.timestamp)}</p>
                </div>
                <div className="flex space-x-2">
                  {alert.status === 'active' && (
                    <Button variant="ghost" size="sm" className="text-red-600">
                      Resolve
                    </Button>
                  )}
                  <Button variant="ghost" size="sm">
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SystemHealthPanel;